import * as THREE from "three";
import { Mesh, Vector2, Vector3, ConeGeometry, MeshStandardMaterial } from "three";

import { LineSegments2 } from 'three/addons/lines/LineSegments2.js';
import { LineGeometry } from 'three/addons/lines/LineGeometry.js';
import { LineMaterial } from 'three/addons/lines/LineMaterial.js';

import LengthDimensionData from "../sceneObjectData/LengthDimensionData";
import { areVectorsEqualWithinTolerance } from "../../utils/geometryUtils";
import Constants from "../../constants";
import LineSegment3d from "../../math/linearAlgebra/lineSegment3d";
import FontProvider from "../../logic/FontProvider";
import LayerMappingsEnum from "../../enums/LayerMappingsEnum";
import LengthDimensionSubGeometryBase from "./LengthDimensionSubGeometryBase";
import { createHitLine } from "../../utils/annotationUtils";
import { getSceneObjectStyle, getSceneObjectStyleName, SceneObjectStyle, SceneObjectStyleEnum } from "../sceneObject/SceneObjectStyle";

export default class LengthDimensionSubGeometry3d extends LengthDimensionSubGeometryBase { 
    
    dimensionLineGeometry: LineGeometry;
    startExtensionLineGeometry: LineGeometry;
    endExtensionLineGeometry: LineGeometry;

    dimensionLine: LineSegments2;
    startExtensionLine!: LineSegments2;
    endExtensionLine!: LineSegments2;
    
    startConeMesh: Mesh;
    endConeMesh: Mesh;
    
    lineMaterial2 = LengthDimensionSubGeometry3d.getLineMaterial(getSceneObjectStyle(SceneObjectStyleEnum.Normal));
    meshMaterial = LengthDimensionSubGeometry3d.getMeshMaterial(getSceneObjectStyle(SceneObjectStyleEnum.Normal));

    private static lineMaterialCache = new WeakMap<SceneObjectStyle, LineMaterial>();
    private static meshMaterialCache = new WeakMap<SceneObjectStyle, MeshStandardMaterial>();

    private static getLineMaterial(style: SceneObjectStyle): LineMaterial {
        let mat = LengthDimensionSubGeometry3d.lineMaterialCache.get(style);
        if (!mat) {
            mat = new LineMaterial({
                color: style.color,
                linewidth: style.lineWidth,
                resolution: new Vector2(window.innerWidth, window.innerHeight),
                dashed: false
            });
            mat.name = `LengthDim3d-${getSceneObjectStyleName(style)}-Line`;
            LengthDimensionSubGeometry3d.lineMaterialCache.set(style, mat);
        }
        return mat;
    }

    private static getMeshMaterial(style: SceneObjectStyle): MeshStandardMaterial {
        let mat = LengthDimensionSubGeometry3d.meshMaterialCache.get(style);
        if (!mat) {
            mat = new MeshStandardMaterial({
                color: style.color,
                metalness: 1.0,
                roughness: 0.2,
                emissive: new THREE.Color(style.color),
                emissiveIntensity: 1.0
            });
            mat.name = `LengthDim3d-${getSceneObjectStyleName(style)}-Mesh`;
            LengthDimensionSubGeometry3d.meshMaterialCache.set(style, mat);
        }
        return mat;
    }

    constructor() {
        super();
        
        this.group.layers.set(LayerMappingsEnum.Annotations3d);
        
        // Build 3d cones
        this.startConeMesh = new Mesh( this.buildConeGeometry(), this.meshMaterial);
        this.startConeMesh.scale.set(   LengthDimensionSubGeometryBase.triangleScale, 
                                        LengthDimensionSubGeometryBase.triangleScale, 
                                        LengthDimensionSubGeometryBase.triangleScale); 
        this.startConeMesh.name = "StartCone";
        this.endConeMesh = this.startConeMesh.clone(); // Clone the start triangle mesh for the end triangle mesh
        this.endConeMesh.name = "EndCone";

        this.startExtensionLineGeometry = new LineGeometry();
        this.startExtensionLine = new LineSegments2(this.startExtensionLineGeometry, this.lineMaterial2);

        this.endExtensionLineGeometry = new LineGeometry();
        this.endExtensionLine = new LineSegments2(this.endExtensionLineGeometry, this.lineMaterial2);
        
        this.dimensionLineGeometry = new LineGeometry();
        this.dimensionLine = new LineSegments2(this.dimensionLineGeometry, this.lineMaterial2);

        // Add parts to the group
        this.group.add(this.startExtensionLine); // Add the line to the group
        this.group.add(this.endExtensionLine); // Add the line to the group
        this.group.add(this.dimensionLine);

        // Invisible, wider hit targets alongside each visible line
        this.group.add(createHitLine(this.startExtensionLineGeometry));
        this.group.add(createHitLine(this.endExtensionLineGeometry));
        this.group.add(createHitLine(this.dimensionLineGeometry));

        this.group.add(this.startConeMesh); // Add the triangle mesh to the scene
        this.group.add(this.endConeMesh); // Add the triangle mesh to the scene

        this.group.add(this.textGroup);

    }

    buildConeGeometry(): ConeGeometry {
        const coneLength = Math.sqrt(3) / 2;
        const coneGeometry = new ConeGeometry(0.2, coneLength, 8);
        coneGeometry.translate(0, -coneLength / 2, 0); // Translate the cone to the correct position
        return coneGeometry;
    }

    // Update start and end triangle meshes
    updateEndMarkers(p1: Vector3, p2: Vector3) {
        const lineVector = new Vector3().subVectors(p1, p2);
        const startDir = lineVector.clone().normalize(); // Calculate the direction of the line
        const endDir = startDir.clone().negate(); // Calculate the direction of the line

        // Flip end markers to outside if line is too small
        if (lineVector.length() < this.endMarkerFlipDistance) {
            startDir.negate();
            endDir.negate();
        }

        LengthDimensionSubGeometryBase.updateEndMarker(this.startConeMesh, p1, startDir); 
        LengthDimensionSubGeometryBase.updateEndMarker(this.endConeMesh, p2, endDir); 
    }

    updateLineDimensions(newData: LengthDimensionData, forceUpdate: boolean): boolean {

        if (this.data && !forceUpdate
            && areVectorsEqualWithinTolerance(this.data.start, newData.start, Constants.UPDATE_GEOMETRY_POINT_EQ_TOLERANCE) 
            && areVectorsEqualWithinTolerance(this.data.end, newData.end, Constants.UPDATE_GEOMETRY_POINT_EQ_TOLERANCE)
            && areVectorsEqualWithinTolerance(this.data.offset, newData.offset, Constants.UPDATE_GEOMETRY_POINT_EQ_TOLERANCE)) {

            return false; // if line dimensions are the same, no need to update
            }

        const dimensionLine = new LineSegment3d(    this.getOffsetPoint(newData.start, newData.offset),
                                                    this.getOffsetPoint(newData.end, newData.offset)); // Get the start point with offset
    
        this.dimensionLineGeometry.setPositions(dimensionLine.points().flatMap(pt => [pt.x, pt.y, pt.z]));
        this.dimensionLine.computeLineDistances();

        // Update markers in both ends of the dimension line
        this.updateEndMarkers(dimensionLine.p1, dimensionLine.p2); 

        // Update extension lines
        this.startExtensionLineGeometry.setPositions([newData.start, this.calculateExtensionLineEndPt(newData.start, dimensionLine.p1)]
                                        .flatMap(pt => [pt.x, pt.y, pt.z]));
        this.endExtensionLineGeometry.setPositions([newData.end, this.calculateExtensionLineEndPt(newData.end, dimensionLine.p2)]
                                        .flatMap(pt => [pt.x, pt.y, pt.z]));
       
        return true;
    }

    setStyle(style: SceneObjectStyle): void {
        if (this.style === style) return;
        const lineMat = LengthDimensionSubGeometry3d.getLineMaterial(style);
        const meshMat = LengthDimensionSubGeometry3d.getMeshMaterial(style);
        this.dimensionLine.material = lineMat;
        this.startExtensionLine.material = lineMat;
        this.endExtensionLine.material = lineMat;
        this.startConeMesh.material = meshMat;
        this.endConeMesh.material = meshMat;
        super.setStyle(style);
    }
}